import type { GetCustomersOverviewsResponse } from '@vy/api-definition';
import { GetCustomersOverviewsResponseSchema } from '@vy/api-definition';
import { purge, withDefault } from '@vy/utils';
import type { QueryClient } from 'react-query';
import { useQuery, useQueryClient } from 'react-query';
import { getPrivateApi } from '../../../services/api';
import { queryKeys } from '../../../services/query-key/query-keys';
import type { CustomUseQueryOptions } from '../requests-types/custom-use-query-options';
import { CUSTOMERS_OVERVIEWS_PAGE_SIZE } from './use-customers-overviews-consts';

const CUSTOMERS_OVERVIEWS_RESOURCE = queryKeys.customersOverviews()[0];

export type UseCustomersOverviewsPayload = Parameters<
  ReturnType<typeof getPrivateApi>['getCustomersOverviews']
>[0];

export function useCustomersOverviews(options?: CustomUseQueryOptions<GetCustomersOverviewsResponse>) {
  return useQuery(queryKeys.customersOverviews(), customerOverviewQuery, {
    ...options,
  });
}

export function prefetchCustomersOverviews(queryClient: QueryClient) {
  return queryClient.prefetchQuery(queryKeys.customersOverviews(), customerOverviewQuery);
}

async function customerOverviewQuery() {
  const api = getPrivateApi();
  const customersOverviews = await api.getCustomersOverviews({
    pageSize: CUSTOMERS_OVERVIEWS_PAGE_SIZE,
  });

  return GetCustomersOverviewsResponseSchema.parse(customersOverviews);
}

export function useCustomersOverviewsForCustomersPage(
  payload: UseCustomersOverviewsPayload = {},
  options?: CustomUseQueryOptions<GetCustomersOverviewsResponse>,
) {
  const queryClient = useQueryClient();

  const payloadValues = purge(Object.values(payload));
  const queryKey = queryKeys.customersOverviews(...payloadValues);

  return useQuery(
    queryKey,
    async ({ signal }) => {
      const api = getPrivateApi();
      await cancelGetCustomersOverviewsQueries(queryClient);

      const getCustomersOverviewsResponse = await api.getCustomersOverviews(
        {
          ...payload,
          pageSize: withDefault(payload.pageSize, CUSTOMERS_OVERVIEWS_PAGE_SIZE),
          page: withDefault(payload.page, 0),
        },
        { signal },
      );

      return GetCustomersOverviewsResponseSchema.parse(getCustomersOverviewsResponse);
    },
    {
      keepPreviousData: true,
      ...options,
    },
  );
}

async function cancelGetCustomersOverviewsQueries(queryClient: QueryClient) {
  await queryClient.cancelQueries({
    predicate: (query) => query.queryKey[0] === CUSTOMERS_OVERVIEWS_RESOURCE,
  });
}
